import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { Fragment } from 'react';

import { Button } from '../Elements';

type ConfirmActionProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onConfirm: () => void;
  isLoading?: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmClassName?: string;
};

export const ConfirmAction = ({
  isOpen,
  setIsOpen,
  onConfirm,
  isLoading,
  title,
  message,
  confirmLabel = 'Proceed',
  confirmClassName = 'bg-darkGreen hover:bg-blue-200',
}: ConfirmActionProps) => {
  const closeModal = () => setIsOpen(false);

  const handleOk = async () => {
    await onConfirm();
    setIsOpen(false);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-10' onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-25' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex items-center justify-center min-h-full p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl'>
                <Dialog.Title
                  as='h3'
                  className='text-lg font-medium leading-6 text-gray-900'
                >
                  {title}
                </Dialog.Title>
                <div className='flex flex-row mt-2'>
                  <ExclamationTriangleIcon className='mr-2 error-icon h-[30px] w-[30px]' />
                  <p className='mt-1 text-sm text-gray-500'>{message}</p>
                </div>

                <div className='flex flex-row items-center justify-center mt-4'>
                  <button
                    type='button'
                    className='inline-flex justify-center px-4 py-2 mr-2 text-sm font-medium text-black rounded-md bg-slate-200 hover:bg-blue-200'
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <Button
                    isLoading={isLoading}
                    type='button'
                    variant='secondary'
                    className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white rounded-md ${confirmClassName}`}
                    onClick={handleOk}
                  >
                    {confirmLabel}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
